import { Check, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { btn } from "@/components/common/btn";
import { updateClient, setClientCustomerNumber } from "@/lib/api";
import { CustomerNumberField } from "@/components/clients/CustomerNumberField";
import { useCanHideTax } from "@/components/common/TaxHiddenBadge";
import { AddressFields } from "@/components/AddressFields";
import { InfoCard, InfoRow } from "@/components/workOrderTab/WOGeneralCard";

const Flag = ({ on, label, testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-emerald-500 text-emerald-600" : "border-slate-200 text-slate-400"}`} data-testid={testId}>
    {on && <Check className="w-3 h-3" />}{label}
  </span>
);

// "General" card on the customer General tab — site, facility type, address and document flags.
// Edit (pencil) unlocks the inputs; Done re-locks. All state still lives in Clients.jsx.
export function ClientGeneralCard({
  selectedClient, setSelectedClient, formData, setFormData, clients, setClients,
  clientSites, selectedSite, sheetLocked, setSheetLocked, facilityTypes,
  handleFacilityTypeChange, loadClients,
}) {
  const canHideTax = useCanHideTax();
  const editing = !sheetLocked;
  const primaryName = (clientSites.find((s) => s.is_primary) || clientSites[0])?.name || "";
  const facility = (selectedSite ? selectedSite.facility_type : selectedClient.facility_type) || "";
  const addr = [formData.address, formData.address_line_2, formData.city, formData.state, formData.zip_code].filter(Boolean).join(", ");
  const mapsUrl = addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : "";

  const saveField = async (patch, okMsg) => {
    setFormData((f) => ({ ...f, ...patch }));
    try {
      await updateClient(selectedClient.id, { ...selectedClient, ...patch });
      setSelectedClient((prev) => ({ ...prev, ...patch }));
      setClients(clients.map((c) => (c.id === selectedClient.id ? { ...c, ...patch } : c)));
      if (okMsg) toast.success(okMsg);
    } catch {
      toast.error("Could not save the change");
    }
  };

  return (
    <InfoCard title="General" testId="client-general-card" className="h-full"
      action={(
        <button type="button" className={btn(editing ? "write" : "nav")} onClick={() => setSheetLocked((v) => !v)} data-testid="btn-toggle-sheet-lock"
          title={editing ? "Lock customer details" : "Unlock to edit customer details"}>
          {editing ? <><Check className="w-3 h-3" /> Done</> : <><Pencil className="w-3 h-3" /> Edit</>}
        </button>
      )}>
      <div className="space-y-2" data-testid="customer-info-panel">
        <InfoRow label="Site" testId="client-site-row">
          {selectedSite ? selectedSite.name : (clientSites.length > 1 ? `All sites${primaryName ? ` · primary: ${primaryName}` : ""}` : (primaryName || "—"))}
        </InfoRow>
        <InfoRow label="Cust #">
          {editing ? (
            <CustomerNumberField value={formData.customer_number || ""} client={selectedClient} disabled={false}
              onSet={async (newNum) => {
                try {
                  const { data } = await setClientCustomerNumber(selectedClient.id, newNum);
                  const saved = data?.customer_number || newNum;
                  setFormData((f) => ({ ...f, customer_number: saved }));
                  setSelectedClient((c) => ({ ...c, customer_number: saved }));
                  if (data?.warning) toast.warning(`Customer # set to ${saved}`, { description: data.warning, duration: 8000 });
                  else toast.success(`Customer # set to ${saved}`);
                  loadClients();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to save Customer #");
                }
              }} />
          ) : <span className="font-mono" data-testid="customer-number-locked">{formData.customer_number || "—"}</span>}
        </InfoRow>
        <InfoRow label="Facility type">
          {editing ? (
            <Select value={facility || "none"} onValueChange={(v) => handleFacilityTypeChange(v === "none" ? "" : v)}>
              <SelectTrigger className="h-7 text-sm w-full" data-testid="select-facility-type-detail"><SelectValue placeholder="-" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {facilityTypes.filter((t) => (t.name || "").trim()).map((t) => <SelectItem key={t.id} value={t.name} className="text-sm">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : <span data-testid="select-facility-type-detail">{facility || "—"}</span>}
        </InfoRow>
        {editing ? (
          <div className="pt-1">
            <div className="text-sm text-slate-500 mb-1">Address</div>
            <AddressFields values={formData} onChange={(partial) => setFormData((prev) => ({ ...prev, ...partial }))} readOnly={false} compact={false} testIdPrefix="input-detail" />
          </div>
        ) : (
          <InfoRow label="Address" testId="client-address-row">
            {addr ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline inline-flex items-center gap-1" data-testid="client-address-link">{addr}<ExternalLink className="w-3 h-3" /></a> : "—"}
          </InfoRow>
        )}
        <InfoRow label="Tax on docs" testId="client-tax-row">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" role="switch" aria-checked={!!formData.hide_tax} disabled={!canHideTax} data-testid="client-hide-tax"
              title={canHideTax ? "Toggle: hide the tax line on this customer's proposals, change orders, work orders, invoices and quotes. Tax is folded into Material — still calculated, charged and remitted." : "Only Ryan or Mary can change this"}
              onClick={() => saveField({ hide_tax: !formData.hide_tax }, !formData.hide_tax ? "New documents for this customer will hide tax" : "New documents for this customer will show tax")}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:cursor-not-allowed ${formData.hide_tax ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>
              <span className={`inline-block h-2 w-2 rounded-full ${formData.hide_tax ? "bg-amber-500" : "bg-slate-300"}`} />
              {formData.hide_tax ? "Hidden on documents" : "Shown on documents"}
            </button>
            {formData.hide_tax && (canHideTax ? (
              <input type="text" value={formData.tax_certificate_no || ""} placeholder="Certificate # (internal only)"
                onChange={(e) => setFormData({ ...formData, tax_certificate_no: e.target.value })}
                onBlur={(e) => saveField({ tax_certificate_no: e.target.value })}
                className="h-7 w-44 rounded border border-slate-200 px-2 text-[11px]" data-testid="client-tax-certificate" />
            ) : (formData.tax_certificate_no && <span className="text-[11px] text-slate-500" data-testid="client-tax-certificate">Cert # {formData.tax_certificate_no}</span>))}
          </div>
        </InfoRow>
        {selectedClient.is_new_lead && <div className="pt-1"><Flag on label="New lead" testId="client-flag-lead" /></div>}
      </div>
    </InfoCard>
  );
}
